const PRODUCT_DETAILS_SELECTOR = "main[data-template*='product'] .product-details";
const INPUT_SELECTOR = "input[type='radio'], input[type='checkbox']";
const ROOT_TEXT_PATTERN = /(buy|save|more|bundle|折|优惠)/i;
const ROW_TEXT_PATTERN = /(buy|save|usd|\$|¥|\/ea|ea|%|free shipping|best value|standard price|折|优惠)/i;
const CUSTOM_ROOT_CLASS = 'ymq-type1';
const SIZE_ROOT_CLASS = 'ymq-size-type1';
const SIZE_INPUT_SELECTOR =
  "input.ymq-option-button-input, input.ymq-option-rc-input, input[type='radio'], input[type='checkbox']";
const TYPE_INPUT_SELECTOR = "input.ymq-shopify-option[name^='Type-']";
const GENERIC_YMQ_BUTTON_INPUT_SELECTOR = "input.ymq-option-button-input, input.ymq-shopify-option";
const NATIVE_VARIANT_GROUP_SELECTOR = '.variant-option--buttons:not(.variant-option--swatches), .variant-option--equal-width-buttons';
const NATIVE_VARIANT_LABEL_SELECTOR = '.variant-option--buttons:not(.variant-option--swatches) .variant-option__button-label';

/**
 * @param {HTMLInputElement} input
 * @returns {boolean}
 */
function isCheckedLike(input) {
  // For radios, rely only on real checked state to prevent stale attribute causing dual-active UI.
  if (input.type === 'radio') return input.checked;

  return (
    input.checked ||
    input.getAttribute('ymq-option-checked') === 'true' ||
    input.getAttribute('data-current-checked') === 'true'
  );
}

/**
 * @param {HTMLElement} element
 * @param {string} property
 * @param {string} value
 */
function setImportantStyle(element, property, value) {
  element.style.setProperty(property, value, 'important');
}

/**
 * @param {Element} detailsRoot
 */
function applySizeSkin(detailsRoot) {
  detailsRoot.querySelectorAll(`.${SIZE_ROOT_CLASS}`).forEach((sizeRoot) => {
    sizeRoot.querySelectorAll('.ymq-option-label, label').forEach((label) => {
      const input = label.querySelector(SIZE_INPUT_SELECTOR);
      const content = label.querySelector('.ymq-option-button-content');
      if (!(input instanceof HTMLInputElement) || !(content instanceof HTMLElement)) return;

      const isChecked = input.checked;

      setImportantStyle(label, 'outline', 'none');
      setImportantStyle(label, 'box-shadow', 'none');
      setImportantStyle(label, 'background', 'transparent');
      setImportantStyle(label, 'border', '0');

      setImportantStyle(content, 'outline', 'none');
      setImportantStyle(content, 'border-radius', '8px');
      setImportantStyle(content, 'border-color', isChecked ? '#d64933' : '#e8e2da');
      setImportantStyle(content, 'background', isChecked ? '#f7ece9' : '#fff');
      setImportantStyle(content, 'box-shadow', isChecked ? 'inset 0 0 0 1px #d64933' : 'none');
      setImportantStyle(content, 'color', '#2f2a25');

      content.querySelectorAll('.ymq-option-button-text, .ymq-option-value').forEach((textNode) => {
        if (!(textNode instanceof HTMLElement)) return;
        setImportantStyle(textNode, 'color', '#2f2a25');
      });
    });
  });
}

/**
 * @param {Element} detailsRoot
 */
function applyTypeSkin(detailsRoot) {
  detailsRoot.querySelectorAll(TYPE_INPUT_SELECTOR).forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    const label = input.closest('.ymq-option-label, label');
    const content = label?.querySelector('.ymq-option-button-content');
    if (!(label instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

    const isChecked = input.checked;

    setImportantStyle(label, 'outline', 'none');
    setImportantStyle(label, 'box-shadow', 'none');
    setImportantStyle(label, 'background', 'transparent');
    setImportantStyle(label, 'border', '0');

    setImportantStyle(content, 'outline', 'none');
    setImportantStyle(content, 'border-radius', '10px');
    setImportantStyle(content, 'border-color', isChecked ? '#d64933' : '#e8e2da');
    setImportantStyle(content, 'background', isChecked ? '#f7ece9' : '#fff');
    setImportantStyle(content, 'box-shadow', isChecked ? 'inset 0 0 0 1px #d64933' : 'none');
    setImportantStyle(content, 'color', '#2f2a25');

    content.querySelectorAll('.ymq-option-button-text, .ymq-option-value').forEach((textNode) => {
      if (!(textNode instanceof HTMLElement)) return;
      setImportantStyle(textNode, 'color', '#2f2a25');
    });
  });
}

/**
 * Keep all YMQ button-style options in one visual system regardless of option name.
 * @param {Element} detailsRoot
 */
function applyGenericYmqButtonSkin(detailsRoot) {
  /** @type {Set<HTMLElement>} */
  const wrappers = new Set();

  detailsRoot.querySelectorAll(GENERIC_YMQ_BUTTON_INPUT_SELECTOR).forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;

    const label = input.closest('.ymq-option-label, label');
    if (!(label instanceof HTMLElement)) return;

    // Skip swatch/image options; they use dedicated visual treatment.
    if (label.querySelector('.ymq-option-swatches-item')) return;

    const content = label.querySelector('.ymq-option-button-content');
    if (!(content instanceof HTMLElement)) return;

    const wrapper = label.closest(
      '.ymq-option-value-wrapper, .ymq-option-values, .ymq-option-list, .ymq-option-items, .ymq-option-value-items, .ymq-option-value-info',
    );
    if (wrapper instanceof HTMLElement) wrappers.add(wrapper);

    const item = label.closest('.ymq-option-value, .ymq-option-item, .ymq-option-value-item, li, div');
    if (item instanceof HTMLElement) {
      setImportantStyle(item, 'margin', '0');
      setImportantStyle(item, 'padding', '0');
      setImportantStyle(item, 'width', 'auto');
      setImportantStyle(item, 'flex', '0 0 auto');
      setImportantStyle(item, 'border', '0');
      setImportantStyle(item, 'background', 'transparent');
      setImportantStyle(item, 'box-shadow', 'none');
    }

    const checked = isCheckedLike(input);

    setImportantStyle(label, 'margin', '0');
    setImportantStyle(label, 'padding', '0');
    setImportantStyle(label, 'display', 'inline-flex');
    setImportantStyle(label, 'align-items', 'center');
    setImportantStyle(label, 'justify-content', 'center');
    setImportantStyle(label, 'min-width', '0');
    setImportantStyle(label, 'min-height', '0');
    setImportantStyle(label, 'border', '0');
    setImportantStyle(label, 'background', 'transparent');
    setImportantStyle(label, 'box-shadow', 'none');
    setImportantStyle(label, 'outline', 'none');

    setImportantStyle(content, 'display', 'inline-flex');
    setImportantStyle(content, 'align-items', 'center');
    setImportantStyle(content, 'justify-content', 'center');
    setImportantStyle(content, 'min-width', '0');
    setImportantStyle(content, 'min-height', '44px');
    setImportantStyle(content, 'padding', '10px 16px');
    setImportantStyle(content, 'border-radius', '10px');
    setImportantStyle(content, 'border-width', '1.5px');
    setImportantStyle(content, 'border-style', 'solid');
    setImportantStyle(content, 'border-color', checked ? '#d64933' : '#e8e2da');
    setImportantStyle(content, 'background', checked ? '#f7ece9' : '#fff');
    setImportantStyle(content, 'box-shadow', checked ? 'inset 0 0 0 1px #d64933' : 'none');
    setImportantStyle(content, 'color', '#2f2a25');
    setImportantStyle(content, 'outline', 'none');

    const disabledSvg = content.querySelector('.ymq-option-disabled-svg');
    if (disabledSvg instanceof HTMLElement) {
      setImportantStyle(disabledSvg, 'display', 'none');
    }

    content.querySelectorAll('.ymq-option-button-text, .ymq-option-value').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportantStyle(node, 'color', '#2f2a25');
      setImportantStyle(node, 'text-align', 'center');
      setImportantStyle(node, 'line-height', '1');
    });
  });

  wrappers.forEach((wrapper) => {
    setImportantStyle(wrapper, 'display', 'flex');
    setImportantStyle(wrapper, 'flex-wrap', 'wrap');
    setImportantStyle(wrapper, 'gap', '10px');
    setImportantStyle(wrapper, 'justify-content', 'flex-start');
    setImportantStyle(wrapper, 'align-items', 'flex-start');
    setImportantStyle(wrapper, 'margin', '0');
    setImportantStyle(wrapper, 'padding', '0');
  });
}

/**
 * Keep native variant buttons aligned with the same button system.
 * @param {Element} detailsRoot
 */
function applyNativeVariantSkin(detailsRoot) {
  detailsRoot.querySelectorAll(NATIVE_VARIANT_GROUP_SELECTOR).forEach((group) => {
    if (!(group instanceof HTMLElement)) return;
    setImportantStyle(group, 'display', 'flex');
    setImportantStyle(group, 'flex-wrap', 'wrap');
    setImportantStyle(group, 'gap', '10px');
    setImportantStyle(group, 'justify-content', 'flex-start');
    setImportantStyle(group, 'align-items', 'flex-start');
    setImportantStyle(group, 'grid-template-columns', 'none');
    setImportantStyle(group, 'margin', '0');
    setImportantStyle(group, 'padding', '0');
  });

  detailsRoot.querySelectorAll(NATIVE_VARIANT_LABEL_SELECTOR).forEach((label) => {
    if (!(label instanceof HTMLElement)) return;
    const input = label.querySelector("input[type='radio'], input[type='checkbox']");
    if (!(input instanceof HTMLInputElement)) return;

    const checked = isCheckedLike(input);

    setImportantStyle(label, 'display', 'inline-flex');
    setImportantStyle(label, 'align-items', 'center');
    setImportantStyle(label, 'justify-content', 'center');
    setImportantStyle(label, 'min-width', '0');
    setImportantStyle(label, 'min-height', '44px');
    setImportantStyle(label, 'padding', '10px 16px');
    setImportantStyle(label, 'border-radius', '10px');
    setImportantStyle(label, 'border-width', '1.5px');
    setImportantStyle(label, 'border-style', 'solid');
    setImportantStyle(label, 'border-color', checked ? '#d64933' : '#e8e2da');
    setImportantStyle(label, 'background', checked ? '#f7ece9' : '#fff');
    setImportantStyle(label, 'box-shadow', checked ? 'inset 0 0 0 1px #d64933' : 'none');
    setImportantStyle(label, 'color', '#2f2a25');
    setImportantStyle(label, 'outline', 'none');
    setImportantStyle(label, 'overflow', 'visible');

    const pill = label.querySelector('.variant-option__button-label__pill');
    if (pill instanceof HTMLElement) {
      setImportantStyle(pill, 'display', 'none');
    }

    const textNode = label.querySelector('.variant-option__button-label__text');
    if (textNode instanceof HTMLElement) {
      setImportantStyle(textNode, 'text-align', 'center');
      setImportantStyle(textNode, 'text-wrap', 'nowrap');
      setImportantStyle(textNode, 'color', '#2f2a25');
    }
  });
}

/**
 * @param {Element} element
 * @returns {string}
 */
function compactText(element) {
  return (element.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * @param {Element[]} nodes
 * @returns {Element[]}
 */
function dedupeNested(nodes) {
  const sorted = [...nodes].sort(
    (a, b) => b.querySelectorAll('*').length - a.querySelectorAll('*').length,
  );
  /** @type {Element[]} */
  const kept = [];
  for (const node of sorted) {
    if (kept.some((candidate) => node.contains(candidate))) continue;
    kept.push(node);
  }
  return kept;
}

/**
 * @param {Element} element
 * @returns {boolean}
 */
function isYmqLikeRoot(element) {
  if (element.classList.contains(CUSTOM_ROOT_CLASS)) return true;

  const inputsCount = element.querySelectorAll(INPUT_SELECTOR).length;
  if (inputsCount < 3 || inputsCount > 40) return false;

  const text = compactText(element);
  if (text.length < 16 || text.length > 3200) return false;
  return ROOT_TEXT_PATTERN.test(text);
}

/**
 * @param {Element} detailsRoot
 * @returns {Element[]}
 */
function findYmqRoots(detailsRoot) {
  const targeted = Array.from(
    detailsRoot.querySelectorAll(
      `.shopify-block, .shopify-app-block, [id*='ymq'], [class*='ymq'], .${CUSTOM_ROOT_CLASS}, variant-picker + *`,
    ),
  ).filter(isYmqLikeRoot);

  if (targeted.length) return dedupeNested(targeted);

  const fallback = Array.from(detailsRoot.querySelectorAll('div, fieldset, form, section, ul, ol')).filter(
    isYmqLikeRoot,
  );
  return dedupeNested(fallback);
}

/**
 * @param {Element} root
 * @param {HTMLInputElement} input
 * @returns {Element | null}
 */
function resolveTierRow(root, input) {
  const preferred = input.closest("label, li, tr, td, [role='radio'], [role='option']");
  if (preferred && preferred !== root && root.contains(preferred)) {
    return preferred;
  }

  let node = input.parentElement;
  while (node && node !== root) {
    const text = compactText(node);
    if (text.length >= 4 && text.length <= 280 && ROW_TEXT_PATTERN.test(text)) {
      const nestedInputsCount = node.querySelectorAll(INPUT_SELECTOR).length;
      if (nestedInputsCount <= 2) return node;
    }
    node = node.parentElement;
  }

  const fallback = input.closest('label, li, tr, td, p, div');
  if (!fallback || fallback === root) return null;
  if (fallback.querySelectorAll(INPUT_SELECTOR).length > 2) return null;
  return fallback;
}

/**
 * @param {Element} root
 */
function markTierRows(root) {
  root.classList.add('ymq-type1-root');

  root.querySelectorAll('.ymq-type1-tier-row, .ymq-type1-summary, .is-active').forEach((node) => {
    node.classList.remove('ymq-type1-tier-row', 'ymq-type1-summary', 'is-active');
  });

  /** @type {Set<Element>} */
  const rows = new Set();
  const checkedRows = new Set();
  const inputs = root.querySelectorAll(INPUT_SELECTOR);

  for (const input of inputs) {
    if (!(input instanceof HTMLInputElement)) continue;
    const row = resolveTierRow(root, input);
    if (!row || row === root) continue;

    rows.add(row);
    if (input.checked) checkedRows.add(row);
  }

  rows.forEach((row) => row.classList.add('ymq-type1-tier-row'));
  checkedRows.forEach((row) => row.classList.add('is-active'));
}

/**
 * @param {Element} detailsRoot
 */
function applySkin(detailsRoot) {
  const roots = findYmqRoots(detailsRoot);
  roots.forEach((root) => markTierRows(root));
  applyGenericYmqButtonSkin(detailsRoot);
  applyNativeVariantSkin(detailsRoot);
}

/**
 * @param {Element} detailsRoot
 */
function setup(detailsRoot) {
  let rafId = 0;
  const scheduleApply = () => {
    if (rafId) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = 0;
      applySkin(detailsRoot);
    });
  };

  applySkin(detailsRoot);

  const observer = new MutationObserver(scheduleApply);
  observer.observe(detailsRoot, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'checked', 'aria-checked'],
  });

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.closest(PRODUCT_DETAILS_SELECTOR)) return;
      scheduleApply();
    },
    true,
  );
}

function boot() {
  const detailsRoot = document.querySelector(PRODUCT_DETAILS_SELECTOR);
  if (!detailsRoot) return;
  setup(detailsRoot);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
